import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from 'urql'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import Button from 'app/components/Button'
import FormInput from 'app/components/form/FormInput'
import FormCombo from 'app/components/form/FormCombo'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { SEARCH_AVAILABLE_QUERY, PURCHASE_MUTATION } from './graphql'
import { PhoneNumberType } from 'app/generated/graphql'
import type { PurchasePhoneNumberInput } from 'app/generated/graphql'
import type { OptionType } from 'types/OptionType'

type PurchaseNumberFormProps = {
  locationId: string
  onSuccess?: () => void
}

const TYPE_OPTIONS: OptionType[] = [
  { id: PhoneNumberType.Voice, name: 'Voice', value: PhoneNumberType.Voice },
  { id: PhoneNumberType.Messaging, name: 'Messaging', value: PhoneNumberType.Messaging },
]

export function PurchaseNumberForm({ locationId, onSuccess }: PurchaseNumberFormProps) {
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])

  const [type, setType] = useState<PhoneNumberType>(PhoneNumberType.Voice)
  const [areaCode, setAreaCode] = useState('')
  const [selectedPhone, setSelectedPhone] = useState<OptionType | undefined>()
  const [label, setLabel] = useState('')

  // Auto-search when area code reaches 3 digits
  const shouldSearch = type === PhoneNumberType.Voice && areaCode.length === 3

  const [searchResult] = useQuery({
    query: SEARCH_AVAILABLE_QUERY,
    variables: { type, areaCode, limit: 5 },
    pause: !shouldSearch,
    context: opsContext,
  })

  const [purchaseResult, executePurchase] = useMutation(PURCHASE_MUTATION)

  const availableNumbers = searchResult.data?.searchAvailablePhoneNumbers ?? []

  const phoneOptions: OptionType[] = availableNumbers.map((num) => ({
    id: num.phone,
    name: num.locality ? `${num.phone} — ${num.locality}` : num.phone,
    value: num.phone,
  }))

  // Reset selection when type changes
  useEffect(() => {
    setSelectedPhone(undefined)
    setAreaCode('')
  }, [type])

  const handleTypeChange = (option: OptionType) => {
    setType(option.value as PhoneNumberType)
  }

  const handlePurchase = async () => {
    const input: PurchasePhoneNumberInput = {
      locationId,
      phone: selectedPhone?.value,
      type,
      roles: type === PhoneNumberType.Voice ? ['ivr'] : ['messaging'],
      label: label || undefined,
    }

    const { data, error } = await executePurchase({ input }, opsContext)
    if (error) {
      logError('purchase failed', error)
      notifyError(error.message)
      return
    }

    const status = data?.purchasePhoneNumber?.provisioningStatus
    if (status === 'ordering') {
      notifySuccess('Number ordered — provisioning in progress')
    } else {
      notifySuccess('Number purchased')
    }
    closeModal()
    // Delay refetch to allow OpenSearch indexing
    setTimeout(() => onSuccess?.(), 1500)
  }

  const canPurchase = type === PhoneNumberType.Messaging || !!selectedPhone

  return (
    <div className="flex w-[600px] flex-col">
      <div className="flex flex-col gap-y-4 px-6 py-4">
        {/* Type selector */}
        <FormCombo
          label="Number Type"
          labelClassName="text-strong text-body-sm font-medium"
          inputClassName="!h-10 !px-2 !py-1 text-body-sm"
          name="type"
          value={TYPE_OPTIONS.find((o) => o.id === type)}
          options={TYPE_OPTIONS}
          onChange={handleTypeChange}
          cols={6}
        />

        {/* Voice: area code → auto-search → select number */}
        {type === PhoneNumberType.Voice && (
          <>
            <FormInput
              label="Area Code"
              labelClassName="text-strong text-body-sm font-medium"
              className="h-10 !px-2 !py-1"
              name="areaCode"
              type="text"
              placeholder="e.g. 512"
              value={areaCode}
              maxLength={3}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))}
              cols={6}
            />

            {shouldSearch && searchResult.fetching && (
              <p className="text-body-sm text-minimal">Searching available numbers...</p>
            )}

            {shouldSearch && !searchResult.fetching && searchResult.error && (
              <div className="mt-[9px] text-body-sm text-red-600">Search failed: {searchResult.error.message}</div>
            )}

            {shouldSearch && !searchResult.fetching && !searchResult.error && phoneOptions.length > 0 && (
              <FormCombo
                label="Available Numbers"
                labelClassName="text-strong text-body-sm font-medium"
                inputClassName="!h-10 !px-2 !py-1 text-body-sm"
                name="selectedPhone"
                placeholder="Select a number"
                value={selectedPhone}
                options={phoneOptions}
                onChange={(option: OptionType) => setSelectedPhone(option)}
                cols={6}
              />
            )}

            {shouldSearch && !searchResult.fetching && !searchResult.error && phoneOptions.length === 0 && (
              <div className="mt-[9px] text-body-sm text-red-600">No numbers available in area code {areaCode}.</div>
            )}
          </>
        )}

        {type === PhoneNumberType.Messaging && (
          <p className="text-body-sm text-minimal">
            Local SMS uses Trackable&apos;s shared registration. Workspace-branded RCS becomes available after its
            separate approval.
          </p>
        )}

        {/* Label */}
        <FormInput
          label="Label"
          labelClassName="text-strong text-body-sm font-medium"
          className="h-10 !px-2 !py-1"
          name="label"
          type="text"
          placeholder="e.g. Main IVR Line"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          cols={6}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t border-default px-6 py-3">
        <Button label="Cancel" mode="secondary" size="sm" onClick={closeModal} />
        <Button
          label="Purchase"
          mode="primary"
          size="sm"
          onClick={handlePurchase}
          disabled={!canPurchase || purchaseResult.fetching}
          loading={purchaseResult.fetching}
        />
      </div>
    </div>
  )
}
